import { PERMISSIONS } from "./permissions";
import type { FlowStatus } from "./types/flows";

/**
 * QL-239 — who is starting or submitting a flow instance:
 * - `app`: a signed-in user, through the advisor UI or the AI chat / MCP
 *   tools. Decided by the user's own permissions.
 * - `public_api`: an integration through /api/v1. The API key acts as its
 *   creator, who may be a designer, so the channel — not the permissions —
 *   is what keeps a preview flow out of the API.
 * - `client`: the client in a public flow session.
 */
export type FlowWorkChannel = "app" | "public_api" | "client";

/** QL-239 — starting a new instance, or submitting an existing one. */
export type FlowWorkAction = "start" | "submit";

/**
 * QL-239 — may this caller start or submit an instance of a flow in `status`?
 * - Active: everyone.
 * - Preview: designers in the app only, for both.
 * - Inactive: nobody starts a new instance, but existing instances can still
 *   be submitted — deactivating a flow does not strand advice in progress.
 * The services enforce it; the web mirrors it to hide or disable actions.
 */
export function canStartOrSubmitFlow(
  status: FlowStatus,
  channel: FlowWorkChannel,
  permissions: readonly string[],
  action: FlowWorkAction,
): boolean {
  if (status === "active") return true;
  if (status === "preview") return channel === "app" && permissions.includes(PERMISSIONS.DESIGNER);
  return action === "submit";
}
